import { Box, Grid, Typography, Avatar, Paper, Divider, useTheme, alpha } from "../../../imports/muiImport";
import {
  useEffect,
  useState,
  useDispatch,
  useSelector,
} from "../../../imports/generalImport";
import { motion } from 'framer-motion';
import {
  PersonOutline,
  EmailOutlined,
  AccountBalanceWallet,
  Verified,
  ShieldOutlined,
} from '@mui/icons-material';
import { fetchUserProfile } from "../../../store/userProfile/action";
import { useAuth } from "../../../hooks/contexts/AuthContext";
import { WalletHold } from "../../../apiCalls/urlFunction";

const MobileProfile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserProfile?.allData);
  const { isLoggedIn } = useAuth();
  const [loading, setLoading] = useState(true);
  const [holdAmount, setHoldAmount] = useState({
    holdAmount: 0,
    availableAmount: 0,
  });
  const getWallet = async () => {
    try {
      const response = await WalletHold();
      setHoldAmount({
        holdAmount: Number(response?.data?.holdAmount || 0),
        availableAmount: Number(response?.data?.availableAmount || 0),
      });
    } catch (error) {
    }
  };


  useEffect(() => {
    if (!user && isLoggedIn) {
      dispatch(fetchUserProfile());
    } else {
      setLoading(false);
    }
    getWallet()
  }, [dispatch, user, isLoggedIn]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const floatAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const pulseGlow = {
    scale: [1, 1.05, 1],
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 2.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  // Profile data - Only 3 fields
  const profileData = [
    {
      label: "Full Name",
      value: user?.firstName || "N/A",
      icon: <PersonOutline sx={{ fontSize: 20 }} />,
      color: '#667eea',
    },
    {
      label: "Email Address",
      value: user?.email || "N/A",
      icon: <EmailOutlined sx={{ fontSize: 20 }} />,
      color: '#f093fb',
    },
    {
      label: "Wallet Balance",
      value: `AED ${user?.walletDatum?.amount || '0'}`,
      icon: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
      color: '#e9b367',
    },
    {
      label: "Wallet Hold Balance",
      value: `AED ${holdAmount?.holdAmount || '0'}`,
      icon: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
      color: '#e9b367',
    },
  ];

  // Get initials for avatar
  const getInitials = () => {
    if (!user?.firstName) return 'G';
    const name = user.firstName;
    const words = name.trim().split(' ');
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
          boxShadow: '0 8px 40px rgba(0,0,0,0.04)',
          position: 'relative',
        }}
      >
        {/* Decorative Gradient Header */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: 80, sm: 100 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 100%)`,
            borderRadius: '4px 4px 0 0',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: '30%',
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
            }}
          />

          {/* Status Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              padding: '4px 12px',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Verified sx={{ fontSize: 14, color: 'white' }} />
            <Typography
              sx={{
                fontSize: '10px',
                color: 'white',
                fontWeight: 600,
                fontFamily: 'Be Vietnam Pro, sans-serif',
                letterSpacing: '0.05em',
              }}
            >
              VERIFIED
            </Typography>
          </Box>
        </Box>

        {/* Profile Content */}
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          {/* Avatar & Name Section */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, sm: 2.5 },
                mb: 3,
                mt: { xs: -5, sm: -5.5 },
                position: 'relative',
                zIndex: 2,
              }}
            >
              {/* Avatar with Glow */}
              <Box sx={{ position: 'relative' }}>
                <motion.div
                  animate={pulseGlow}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha('#e9b367', 0.2)} 0%, transparent 70%)`,
                  }}
                />

                <motion.div
                  animate={floatAnimation}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <Avatar
                    src={user?.logoUrl}
                    sx={{
                      width: { xs: 65, sm: 75 },
                      height: { xs: 65, sm: 75 },
                      border: '3px solid white',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 100%)`,
                    }}
                  >
                    {getInitials(user?.firstName || "N/A")}
                  </Avatar>
                </motion.div>
              </Box>
            </Box>
          </motion.div>

          <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.primary.main, 0.06) }} />

          {/* Profile Details - 3 Fields in Grid */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={2}>
              {profileData?.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <motion.div
                    whileHover={{ scale: 1.01, x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        background: alpha(item.color, 0.04),
                        border: `1px solid ${alpha(item.color, 0.08)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: alpha(item.color, 0.06),
                          borderColor: alpha(item.color, 0.15),
                          boxShadow: `0 4px 20px ${alpha(item.color, 0.06)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            background: alpha(item.color, 0.1),
                            color: item.color,
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: alpha(theme.palette.text.secondary, 0.6),
                              fontFamily: 'Be Vietnam Pro, sans-serif',
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            {item.label}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: { xs: '15px', sm: '16px' },
                              fontFamily: 'Onest, Be Vietnam Pro, sans-serif',
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              mt: 0.25,
                              wordBreak: 'break-word',
                            }}
                          >
                            {loading ? (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  width: 60,
                                  height: 8,
                                  borderRadius: 4,
                                  background: alpha(theme.palette.text.primary, 0.06),
                                  animation: 'shimmer 1.5s ease-in-out infinite',
                                }}
                              />
                            ) : (
                              item.value
                            )}
                          </Typography>
                        </Box>

                        {/* Wallet Balance Highlight */}
                        {item.label === "Wallet Balance" && !loading && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              background: alpha(item.color, 0.12),
                              border: `1px solid ${alpha(item.color, 0.15)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                color: item.color,
                                fontFamily: 'Onest, Be Vietnam Pro, sans-serif',
                              }}
                            >
                              {'AED'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>
      </Paper>

      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes progressAnim {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </motion.div>
  );
};

export default MobileProfile
